'use client';

import React, { useCallback, useMemo, useState } from 'react';
import type { ChatSummary } from '../../../types/index.js';
import { ChatItem } from './ChatItem.js';
import { EmptyState } from './EmptyState.js';
import { LoadMoreTrigger } from './LoadMoreTrigger.js';
import { ScrollArea } from '@west-tennessee-consulting/ui/components/scroll-area';
import { FolderPlus, Folder } from 'lucide-react';
import type { TabType } from '../types.js';
import { SIDEBAR_LAYOUT } from '../../../styles/sidebar-layout.js';
import { useChatOrganization } from '../hooks/useChatOrganization.js';

interface ChatListProps {
  filteredChats: ChatSummary[];
  selectedChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onRestoreChat: (chatId: string) => void;
  onDeleteChat?: (chatId: string) => void;
  activeTab: TabType;
  loadTriggerRef: React.RefObject<HTMLDivElement | null>;
  hasMoreChats: boolean;
  isLoadingMoreChats: boolean;
  truncateTitle: (title: string, maxLength?: number) => string;
  formatDate: (dateString: string) => string;
  compactMode?: boolean;
}

/**
 * Chat list container. Chats render as single-line rows. When the user has
 * created projects (interim client-side grouping), chats assigned to a project
 * are grouped under a project header; everything else falls under "Recent".
 */
export const ChatList: React.FC<ChatListProps> = React.memo(
  ({
    filteredChats,
    selectedChatId,
    onSelectChat,
    onRestoreChat,
    onDeleteChat,
    activeTab,
    loadTriggerRef,
    hasMoreChats,
    isLoadingMoreChats,
    truncateTitle,
    formatDate,
    compactMode = false,
  }) => {
    const org = useChatOrganization();
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');

    const submitNewProject = useCallback(() => {
      const name = newName.trim();
      if (name) org.createProject(name);
      setNewName('');
      setCreating(false);
    }, [newName, org]);

    const { grouped, ungrouped, hasGroups } = useMemo(() => {
      const byProject = new Map<string, ChatSummary[]>();
      const rest: ChatSummary[] = [];
      const known = new Set(org.projects.map((p) => p.id));
      for (const chat of filteredChats) {
        const pid = org.projectIdOf(chat.id);
        if (pid && known.has(pid)) {
          const arr = byProject.get(pid) ?? [];
          arr.push(chat);
          byProject.set(pid, arr);
        } else {
          rest.push(chat);
        }
      }
      // Only surface projects that actually contain chats in this tab.
      const groups = org.projects
        .map((project) => ({ project, chats: byProject.get(project.id) ?? [] }))
        .filter((g) => g.chats.length > 0);
      return { grouped: groups, ungrouped: rest, hasGroups: groups.length > 0 };
    }, [filteredChats, org.projects, org.projectIdOf]);

    const renderChat = useCallback(
      (chat: ChatSummary) => (
        <ChatItem
          key={chat.id}
          chat={chat}
          isSelected={selectedChatId === chat.id}
          onSelectChat={onSelectChat}
          onRestoreChat={onRestoreChat}
          onDeleteChat={onDeleteChat}
          truncateTitle={truncateTitle}
          formatDate={formatDate}
          compactMode={compactMode}
        />
      ),
      [selectedChatId, onSelectChat, onRestoreChat, onDeleteChat, truncateTitle, formatDate, compactMode]
    );

    return (
      <ScrollArea className={SIDEBAR_LAYOUT.chatList}>
        {filteredChats.length === 0 ? (
          <EmptyState activeTab={activeTab} />
        ) : (
          <div className={compactMode ? 'p-2' : 'p-3'}>
            {/* New project affordance — quiet until used. */}
            <div className="mb-1.5">
              {creating ? (
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onBlur={submitNewProject}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitNewProject();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setNewName('');
                      setCreating(false);
                    }
                  }}
                  placeholder="Project name…"
                  aria-label="New project name"
                  className="w-full rounded-md border border-primary/50 bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <FolderPlus className="h-3.5 w-3.5" aria-hidden />
                  New project
                </button>
              )}
            </div>

            {/* Project groups */}
            {grouped.map(({ project, chats }) => (
              <section key={project.id} className="mb-2">
                <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Folder className="h-3.5 w-3.5" aria-hidden />
                  <span className="truncate">{project.name}</span>
                  <span className="ml-auto font-normal opacity-70">{chats.length}</span>
                </div>
                <div className="space-y-0.5">{chats.map(renderChat)}</div>
              </section>
            ))}

            {/* Ungrouped / Recent */}
            {ungrouped.length > 0 && (
              <section>
                {hasGroups && (
                  <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Recent
                  </div>
                )}
                <div className="space-y-0.5">{ungrouped.map(renderChat)}</div>
              </section>
            )}

            <LoadMoreTrigger
              ref={loadTriggerRef}
              hasMoreChats={hasMoreChats}
              isLoadingMoreChats={isLoadingMoreChats}
            />
          </div>
        )}
      </ScrollArea>
    );
  }
);

ChatList.displayName = 'ChatList';
